import axios from "axios";
import { stubFalse } from "lodash";
import {
  API_LOCAL_IP,
  API_SERVICE_BACKEND,
  API_SERVICE_BACKEND_2,
} from "src/config";
import { deepCopy } from "src/utils/deep-copy";
// import { companies, company } from './data';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

class JobsApi {
  userCampaignsCache = new Map();

  generateCacheKey = (email, search, page, limit, status, sortKey, sortValue, fields) => {
    const allStatus = ["active", "draft", "archive", "paused"];
    const statusArray = status === "all" ? allStatus : typeof(status) === "string" ? [status] : status;
    const normalizedStatus = Array.isArray(statusArray) ? statusArray.sort().join(',') : statusArray;
    return `${email}|${search}|${page}|${limit}|${normalizedStatus}|${sortKey}|${sortValue}|${fields}`;
  };

  clearUserCampaignsCache = (email = null) => {
    if (email) {
      const keysToDelete = [];
      this.userCampaignsCache.forEach((value, key) => {
        if (key.startsWith(`${email}|`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.userCampaignsCache.delete(key));
    } else {
      this.userCampaignsCache.clear();
    }
  };
  getCampaignDetails = async (campaignId, isSaveAsDraft, isCopy = "") => {
    const url =
      isSaveAsDraft === "true"
        ? `${API_SERVICE_BACKEND}/filterSaveAsDraftCampaigns`
        : `${API_SERVICE_BACKEND}/filterCampaigns`;
    const res = await axios.get(url, {
      params: { _id: campaignId, isCopy: isCopy },
    });
    if (res.status === 200) {
      return res.data;
    } else {
      throw new Error(res.data);
    }
  };

  getCampaignStats = async (campaignId) => {
    const res = await axios.get(
      `${API_SERVICE_BACKEND}/getStats/${campaignId}`
    );
    if (res.status === 200) {
      return res.data;
    } else {
      throw new Error(res.data);
    }
  };

  getDashBoardStats = async (campaignId) => {
    const res = await axios.get(
      `${API_SERVICE_BACKEND}/getDashBoard/${campaignId}`
    );
    if (res.status === 200) {
      return res.data;
    } else {
      throw new Error(res.data);
    }
  };

  getUserCampaigns = async (
    email,
    search = "",
    page = 0,
    limit = 0,
    status = "all",
    sortKey = "",
    sortValue = "",
    fields = "",
    signal
  ) => {
    const cacheKey = this.generateCacheKey(email, search, page, limit, status, sortKey, sortValue, fields);
    
    if (!signal) {
      const cachedData = this.userCampaignsCache.get(cacheKey);
      if (cachedData) {
        const now = Date.now();
        if (now - cachedData.timestamp < CACHE_TTL) {
          return deepCopy(cachedData.data);
        } else {
          this.userCampaignsCache.delete(cacheKey);
        }
      }
    }

    const allStatus = ["active", "draft", "archive", "paused"];
    const statusArray = status === "all" ? allStatus : typeof(status) === "string" ? [status] : status;
    const statusStr = statusArray?.map((str) => `status[]=${str}`)?.join("&")
    try {
      const res = await axios.get(
        `${API_SERVICE_BACKEND}/userCampaigns/${encodeURIComponent(email)}?search=${encodeURIComponent(
          search
        )}&page=${page}&limit=${limit}&${statusStr}&sortKey=${status === "archive" &&
          sortKey !== "responsibility" &&
          sortKey === "updatedAt"
          ? "lastSavedAt"
          : sortKey
        }&sortValue=${sortValue}&selectFields=${fields}`, {signal}
      );

      if (res.status === 200) {
        if (!signal) {
          this.userCampaignsCache.set(cacheKey, {
            data: res.data,
            timestamp: Date.now()
          });
        }
        return res.data;
      } else {
        throw new Error(res.data);
      }
    } catch (error) {
      if (!signal) {
        const cachedData = this.userCampaignsCache.get(cacheKey);
        if (cachedData) {
          return deepCopy(cachedData.data);
        }
      }
      console.log("error", error);
      return undefined;
    }
  };

  getCampaignLeads = async (
    campaignId,
    page,
    limit = 100,
    abortController,
    email,
    sortOptions,
    exportBool
  ) => {
    // //console.log(campaignId)
    console.warn(sortOptions);
    const res = await axios.get(
      // `http://192.168.18.60:8080/api/v1/main/campaignLeads/${campaignId}`,
      `${API_SERVICE_BACKEND}/campaignLeads/${campaignId}`,
      {
        params: {
          email,
          page,
          limit,
          sortJSON: encodeURI(JSON.stringify(sortOptions)),
          exportBool,
        },
        signal: abortController?.signal,
      }
    );
    if (res.status === 200) {
      return [...res.data.leads, { count: res.data.count }];
    } else {
      throw new Error(res.data);
    }
  };

  getUserSaveAsDraftCampaigns = async (
    email,
    search = "",
    page,
    limit,
    sortKey = "",
    sortValue = "",
    fullResponse = false,
    fields = ""
  ) => {
    // //console.log(email)
    try {
      const res = await axios.get(
        `${API_SERVICE_BACKEND}/userSaveAsDraftCampaigns/${email}?search=${encodeURIComponent(
          search
        )}&page=${page}&limit=${limit}&sortKey=${sortKey}&sortValue=${sortValue}&selectFields=${fields}`
      );
      if (res.status === 200) {
        return fullResponse ? res.data : res.data.data;
      } else {
        throw new Error(res.data.data);
      }
    } catch (error) {
      console.log("error", error);
    }
  };
  getCampaignLeadsV2 = async (
    campaignId,
    page,
    limit = 100,
    abortController,
    email,
    sortOptions,
    exportBool,
    search,
    dateSelect,
    isEmailSent = false,
    stage = "",
    isCounts = true,
    multiChannel = {
      sources: [],
      statuses: [],
    }
  ) => {
    const payload = {
      email,
      page,
      limit,
      isEmailSent,
      sortJSON: encodeURI(JSON.stringify(sortOptions)),
      exportBool,
      search,
      stage,
      isCounts,
      sources: multiChannel?.sources,
      statuses: multiChannel.statuses,
      campaignId: campaignId,
    };
    if (dateSelect.from && dateSelect.to) {
      payload.dateSelect = dateSelect;
    }
    // //console.log(campaignId)
    console.warn(sortOptions);
    const res = await axios.post(
      `${API_SERVICE_BACKEND}/campaignLeadsSearch`, payload,
      {
        signal: abortController?.signal,
      }
    );
    if (res.status === 200) {
      return { leads: res.data?.data?.leads, metaData: res.data?.data?.metaData };
    } else {
      throw new Error(res.data);
    }
  };

  getCampaignContacts = async (
    campaignId,
    page,
    limit = 100,
    abortController,
    email,
    sortOptions,
    exportBool,
    search,
    dateRange,
    isEmailSent = false,
    stage = "",
    isCounts = true,
    filters,
    sortJSON,
    multiChannel = "all"
  ) => {
    const payload = {
      email,
      page,
      limit,
      isEmailSent,
      sortJSON: sortJSON.sortJSON,
      exportBool,
      search,
      stage,
      isCounts,
      sources: multiChannel?.sources,
      statuses: multiChannel.statuses,
      campaignId: campaignId,
      filters,
      multiChannel: multiChannel,

    };
    if (dateRange.from && dateRange.to) {
      payload.dateRange = dateRange;
    }
    // //console.log(campaignId)
    console.warn(sortOptions);
    const res = await axios.post(
      `${API_SERVICE_BACKEND}/campaignLeadsSearch_v2`, { payload },
      {
        signal: abortController?.signal,
      }
    );
    if (res.status === 200) {
      return { leads: res.data.data.leads, metaData: res.data.data.metaData };
    } else {
      throw new Error(res.data);
    }
  };

  getCampaignLeadsExport = async (
    campaignId,
    page,
    limit = 100,
    abortController,
    email,
    sortOptions,
    exportBool,
    search,
    dateSelect,
    isEmailSent = false,
    stage = "",
    isCounts = true,
    multiChannel = {
      sources: [],
      statuses: [],
    }
  ) => {
    const payload = {
      email,
      page,
      limit,
      isEmailSent,
      sortJSON: encodeURI(JSON.stringify(sortOptions)),
      exportBool,
      search,
      stage,
      isCounts,
      sources: multiChannel?.sources,
      statuses: multiChannel.statuses,
      campaignId: campaignId,
    };
    if (dateSelect.from && dateSelect.to) {
      payload.dateSelect = dateSelect;
    }
    // //console.log(campaignId)
    console.warn(sortOptions);
    const res = await axios.post(
      `${API_SERVICE_BACKEND}/export/campaignLeadsSearch`, payload,
      {
        signal: abortController?.signal,
      }
    );
    if (res.status === 200) {
      // it will return csv file
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `campaign_leads_${campaignId}.csv`);
      document.body.appendChild(link);
      link.click();

    } else {
      throw new Error(res.data);
    }
  };

  getChartData = async (email, campaignIds, params) => {
    // //console.log(campaignId)
    const res = await axios.post(`${API_SERVICE_BACKEND}/dashboard-chart`, {
      clientEmail: email,
      campaignIds,
      ...(params ? params : {}),
    });
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data);
    }
  };

  getMultichannelChartData = async (email, campaignIds, params) => {
    // //console.log(campaignId)
    const res = await axios.post(
      `${API_SERVICE_BACKEND}/dashboard-multichannel-chart`,
      {
        clientEmail: email,
        campaignIds,
        ...(params ? params : {}),
      }
    );
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data);
    }
  };

  getOutboundCallChartData = async (email, campaignIds, params) => {
    // //console.log(campaignId)
    const res = await axios.post(
      `${API_SERVICE_BACKEND}/dashboard-outbound-call-chart`,
      {
        clientEmail: email,
        campaignIds,
        ...(params ? params : {}),
      }
    );
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data);
    }
  };

  updateCampaign = async (campaignId, payload) => {
    // //console.log(campaignId, payload);
    try {
      const res = await axios.patch(`${API_SERVICE_BACKEND}/updateCampaign`, {
        id: campaignId,
        payload,
      });
      if (res.status === 204) {
        return true;
      }
    } catch (e) {
      console.log(e.message);
    }
    return false;
  };

  getLeadsTotalData = async (id, dateRange) => {
    const res = await axios.post(`${API_SERVICE_BACKEND}/getLeadsTotalData`, {
      campaignIds: id,
      dateRange: dateRange,
    });
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data);
    }
  };
  getLeadsCount = async (campaignId) => {
    try {
      const res = await axios.get(
        // `${API_SERVICE_BACKEND}/leadsCount?campaignIds=${campaignId}`
        // `${API_SERVICE_BACKEND}/leadsCount?campaignIds=${campaignId}`
        `${API_SERVICE_BACKEND}/getTooltipData`,
      {
        params: { campaignId }
      }
      );
      if (res.data) {
        return res.data;
      }
    } catch (error) {
      console.error("Error fetching campaign progress:", error);
    } 
  };
  getInboundCallAgents = async (user_email) => {
    try {
      const res = await axios.post(
        `${API_SERVICE_BACKEND}/inbound-agents`
        ,{user_email}
      );
      if (res.data) {
        return res.data;
      }
    } catch (error) {
      console.error("Error fetching inbound call agents:", error);
    } 
  };
  getInboundLeadsData=async(campaignId, email, dateSelect,page,limit=100,sortOptions,alignment)=>{
    try {
      const res = await axios.post(
        `${API_SERVICE_BACKEND}/inbound-leads-data`
        ,{agentIds:campaignId, dateSelect,page,limit, sortJSON: encodeURI(JSON.stringify(sortOptions)),alignment, email}
      );
       if (res.status === 200) {
      return { leads: res.data?.data?.leads, metaData: res.data?.data?.metaData };
    } else {
      throw new Error(res.data);
    }
    } catch (error) {
      console.error("Error fetching inbound call agents:", error);
    } 
  }
  getInboundLeadsCampaigns=async(campaignIds,email,dateRange)=>{
    try {
      const res = await axios.post(
        `${API_SERVICE_BACKEND}/inbound-leads-campaigns`,
        {
          email,
          campaignIds,
          dateRange
        }
      );
      if (res.status === 200) {
        return res.data;
      } else {
        throw new Error(res.data);
      }
    } catch (error) {
      console.error("Error fetching inbound leads campaigns:", error);
    } 
  }
}
export const jobsApi = new JobsApi();
